import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { InterpreterConversation } from './InterpreterConversation';
import type { SpeechSettings } from './InterpreterSettings';
import { labels, phases } from './interpreterPage.constants';
import { interpreterPageStyles as styles } from './interpreterPage.styles';
import type { useInterpreter } from './useInterpreter';

interface Props {
  interpreter: ReturnType<typeof useInterpreter>;
  speaker: 'doctor' | 'patient';
  onSwitchSpeaker: () => void;
  onBegin: () => void;
  onFinish: () => void;
  onReplay: () => void;
  onContinueWithoutSpeech: () => void;
  newInterview: () => void;
  settings: SpeechSettings;
  onSettingsChange: (settings: SpeechSettings) => void;
}
export const InterpreterSessionControls = ({
  interpreter,
  speaker,
  onSwitchSpeaker,
  onBegin,
  onFinish,
  onReplay,
  onContinueWithoutSpeech,
  newInterview,
  settings,
  onSettingsChange,
}: Props) => {
  const { record, phase } = interpreter;
  if (!record) return null;
  const ended = record.config.phase === 'ended';
  const recording = phase === 'speaking';
  const paused = phase === 'paused';
  const busy = !['idle', 'speaking', 'paused'].includes(phase) || interpreter.audioSaving;
  const awaitingBegin = !ended && phase === 'idle' && !interpreter.handoffPending;
  const speakingLanguage = speaker === 'doctor' ? record.config.clinician_language : record.config.patient_language;
  const currentRows = record.utterances.filter((row) => row.turn_id === record.config.turn_id);
  const playbackReady = currentRows.length > 0 && currentRows.every((row) => row.translation_status === 'ready');
  const status = ended
    ? phases.ended
    : paused
      ? phases.paused
      : recording
        ? phases.speaking
        : phase === 'playing'
          ? phases.playback
          : phase === 'summarizing'
            ? 'Bý til nótu…'
            : awaitingBegin
              ? 'Ýttu á „Byrja tal“'
              : interpreter.handoffPending && phase === 'idle'
                ? 'Ljúktu þýðingu eða spilaðu upplestur til að halda áfram'
                : phases.processing;
  return (
    <section className={styles.controlsCard} aria-label="Túlkað samtal">
      {interpreter.audioRejected && (
        <p role="alert">Ekki var hægt að vista hluta hljóðsins. Talritið er áfram aðgengilegt.</p>
      )}
      {interpreter.audioError && (
        <div role="alert" className="space-y-2">
          <p>Ekki tókst að vista hljóðið. Haltu síðunni opinni og reyndu aftur.</p>
          <Button variant="outline" disabled={busy || recording} onClick={() => void interpreter.retryAudio()}>
            Reyna hljóðvistun aftur
          </Button>
        </div>
      )}
      {interpreter.audioUrl && (
        <div className="space-y-1">
          <Label htmlFor="interpreter-recorded-audio">Upptaka viðtals</Label>
          {/* biome-ignore lint/a11y/useMediaCaption: The complete bilingual transcript is displayed below. */}
          <audio id="interpreter-recorded-audio" aria-label="Upptaka viðtals" controls src={interpreter.audioUrl} />
        </div>
      )}
      <div aria-live="polite" className={styles.phase}>
        {status}
        {!ended && !busy ? ` · ${labels[speakingLanguage]}` : ''}
      </div>
      <div className={styles.actions}>
        {awaitingBegin && <Button onClick={onBegin}>Byrja tal</Button>}
        {!ended && (
          <Button disabled={!(recording || paused)} onClick={onFinish}>
            Enda tal
          </Button>
        )}
        {!ended && (
          <Button variant="outline" disabled={!awaitingBegin} onClick={onSwitchSpeaker}>
            Skipta um ræðumann
          </Button>
        )}
        {paused && (
          <Button variant="outline" onClick={interpreter.resumeCapture}>
            Halda áfram
          </Button>
        )}
        {!ended && record.utterances.length > 0 && (
          <Button variant="outline" disabled={busy || recording || paused || !playbackReady} onClick={onReplay}>
            Spila upplestur
          </Button>
        )}
        {!ended && interpreter.handoffPending && playbackReady && (
          <Button variant="outline" disabled={busy || recording || paused} onClick={onContinueWithoutSpeech}>
            Halda áfram án upplesturs
          </Button>
        )}
        {!ended ? (
          <Button variant="outline" disabled={busy} onClick={() => void interpreter.end()}>
            Ljúka viðtali
          </Button>
        ) : (
          <Button onClick={newInterview}>Nýtt túlkunarviðtal</Button>
        )}
      </div>
      <div className={styles.modeRow}>
        <Label htmlFor="live-turn-mode">Lotuskipting</Label>
        <Select value="manual" disabled={ended || busy || recording || paused}>
          <SelectTrigger id="live-turn-mode" className={styles.compactSelect}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Handvirk</SelectItem>
            <SelectItem value="automatic" disabled>
              Sjálfvirk (síðar)
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className={styles.settingsStack}>
        {(
          [
            ['speakClinician', record.config.clinician_language],
            ['speakPatient', record.config.patient_language],
          ] as const
        ).map(([key, language]) => (
          <div key={key} className={styles.settingsRow}>
            <Label htmlFor={`live-${key}`} className={styles.settingLabel}>
              Upplestur ({labels[language]})
            </Label>
            <Switch
              id={`live-${key}`}
              checked={settings[key]}
              disabled={ended || busy || recording || paused}
              onCheckedChange={(checked) => onSettingsChange({ ...settings, [key]: checked })}
            />
          </div>
        ))}
      </div>
      <InterpreterConversation
        record={record}
        busy={busy || recording || paused}
        partial={interpreter.partial}
        onRetry={(row) => void interpreter.retry(row)}
      />
    </section>
  );
};
